<template>
  <button :class="['file-button', variant]" @click="$emit('click')" :disabled="disabled || loading">
    <span v-if="loading" class="spinner"></span>
    <img
      v-else-if="showIcon"
      :src="iconSrc"
      :alt="iconAlt"
      class="button-icon"
      :width="iconWidth"
      :height="iconHeight"
    />
    <span class="button-text">{{ text }}</span>
  </button>
</template>

<script setup lang="ts">
interface Props {
  text: string
  variant?: 'primary' | 'secondary' | 'success' | 'cancel' | 'accept' | 'transparent'
  showIcon?: boolean
  iconSrc?: string
  iconAlt?: string
  iconWidth?: number
  iconHeight?: number
  disabled?: boolean
  loading?: boolean
}

withDefaults(defineProps<Props>(), {
  variant: 'primary',
  showIcon: false,
  iconSrc: '',
  iconAlt: '',
  iconWidth: 24,
  iconHeight: 24,
  disabled: false,
  loading: false,
})

defineEmits<{
  click: []
}>()
</script>

<style scoped>
.file-button {
  /* Auto layout */
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 8px 16px;
  gap: 4px;

  width: auto;
  height: 40px;

  /* Alpha Blue/400 */
  background: #003896;
  /* Botón Idle */
  box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.15);
  border-radius: 30px;
  border: none;
  cursor: pointer;

  /* Inside auto layout */
  flex: none;
  order: 0;
  flex-grow: 0;
  transition: all 0.2s ease;
}

.file-button:hover {
  background: #002a6b;
  transform: translateY(-1px);
  box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.2);
}

.file-button:active {
  transform: translateY(0);
  box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.15);
}

.file-button:disabled {
  background: #ccc;
  color: #666;
  cursor: not-allowed;
  opacity: 0.6;
  transform: none;
}

.spinner {
  border: 2px solid #f3f3f3;
  border-top: 2px solid #003896;
  border-radius: 50%;
  width: 18px;
  height: 18px;
  animation: spin 1s linear infinite;
  margin-right: 8px;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.button-icon {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;

  /* Inside auto layout */
  flex: none;
  order: 0;
  flex-grow: 0;
}

.button-text {
  width: auto;
  height: 22px;
  white-space: nowrap;

  /* Web/Button */
  font-family: 'Montserrat', sans-serif;
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 140%;
  text-align: center;

  /* Inside auto layout */
  flex: none;
  order: 1;
  flex-grow: 0;
}

.file-button.primary {
  background: #9cd41c;
  color: #1a1a1a;
}

.file-button.primary:hover {
  background: #7bb516;
}

.file-button.secondary {
  background: #003896;
  color: #fff;
}

.file-button.secondary:hover {
  background: #002a70;
}

.file-button.success {
  background: #9cd41c;
  color: #fff;
}

.file-button.success:hover {
  background: #7bb516;
}

.file-button.cancel {
  background: #f5f5f5;
  color: #1a1a1a;
}

.file-button.cancel:hover {
  background: #e0e0e0;
}

.file-button.accept {
  background: #d0342c;
  color: #fff;
}

.file-button.accept:hover {
  background: #a02722;
}

.file-button.transparent {
  background: transparent;
  color: #1a1a1a;
}
</style>
